'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import { isEmpty } from 'ramda';
import { type ReactElement, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as z from 'zod';
import { Button } from '@ironhack/design-system2/components/button';
import { Flex, FlexProps } from '@ironhack/design-system2/components/flex';

import { sendEvent } from '@/lib/gtm';
import { SelectField } from '@/app/components/client/select-field';
import { normalizeUrlEntry } from '@/app/urls/url-mapping';
import { type GaPageData } from '@/types';
import { type HeroForm, type SelectorOptions } from './hero-form';

type FormData = {
  campus: string;
  course: string;
};

type Props = {
  form: HeroForm;
  selectorOptions: SelectorOptions;
  gaPageData: GaPageData;
} & FlexProps;

const getSchema = (pathMap: SelectorOptions['pathMap']) =>
  z
    .object({
      campus: z.string().length(3),
      course: z.string().length(2),
    })
    .required()
    .refine(({ campus, course }) => pathMap[`${campus}${course}`]);

export const HeroFormClient = (props: Props): ReactElement => {
  const {
    form: { buttonText, campusPlaceholder, coursePlaceholder },
    gaPageData: { gaCategory, gaPageType },
    selectorOptions: { campuses, courses, pathMap },
    ...flexProps
  } = props;
  const formSchema = getSchema(pathMap);
  const formMethods = useForm<FormData>({
    defaultValues: {
      campus: '',
      course: '',
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: zodResolver(formSchema),
  });
  const {
    formState: { errors, isSubmitSuccessful, isSubmitted, isSubmitting },
    handleSubmit,
    trigger,
    reset,
  } = formMethods;

  const [externalLinkOpen, setExternalLinkOpen] = useState(false);

  useEffect(() => {
    if (isSubmitSuccessful && externalLinkOpen) {
      reset();
      setExternalLinkOpen(false);
    }
  }, [externalLinkOpen, isSubmitSuccessful, reset]);

  const router = useRouter();
  const onSubmit = ({ campus, course }: FormData) => {
    sendEvent({
      eventCategory: gaCategory,
      eventAction: 'find your course',
      eventLabel: `${campus}::${course}`,
      pageType: gaPageType,
    });

    const path = normalizeUrlEntry(pathMap[`${campus}${course}`]);
    if (path.isExternal) {
      window.open(path.url, '_blank');
      setExternalLinkOpen(true);
    } else {
      router.push(path.url);
    }
  };

  return (
    <FormProvider {...formMethods}>
      <Flex
        as="form"
        bg="fill.white"
        borderRadius="lg"
        color="text.secondary"
        direction={['column', null, 'row']}
        gap={2}
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        padding={2}
        {...flexProps}
      >
        <SelectField
          flex={1}
          isRequired
          name="course"
          onChange={() => trigger()}
          options={courses.map((course) => ({
            key: course.code,
            text: course.name,
          }))}
          placeholder={coursePlaceholder}
        />
        <SelectField
          flex={1}
          isRequired
          name="campus"
          onChange={() => trigger()}
          options={campuses.map((campus) => ({
            key: campus.code,
            text: campus.name,
          }))}
          placeholder={campusPlaceholder}
        />
        <Button
          flex={1}
          isDisabled={isSubmitted || !isEmpty(errors)}
          isLoading={isSubmitting || isSubmitSuccessful}
          px={0}
          type="submit"
        >
          {buttonText}
        </Button>
      </Flex>
    </FormProvider>
  );
};
